import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { ArrowDownRight } from 'lucide-react';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'motion/react';
import { siteConfig } from '../../config/site';
import { buildWhatsAppUrl } from '../../lib/whatsapp';
import { ButtonLink } from '../ui/ButtonLink';

function canUsePointerDepth(reduced: boolean | null) {
  return typeof window !== 'undefined'
    && !reduced
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    && window.matchMedia('(prefers-reduced-motion: no-preference)').matches
    && window.innerWidth > 1050;
}

export function HeroSection() {
  const reduced = useReducedMotion();
  const [pointerEligible] = useState(() => canUsePointerDepth(reduced));
  const [heroEntered, setHeroEntered] = useState(false);
  const heroSectionRef = useRef<HTMLElement>(null);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const frontX = useTransform(pointerX, [-1, 1], [-8, 8]);
  const frontY = useTransform(pointerY, [-1, 1], [-6, 6]);
  const rearX = useTransform(pointerX, [-1, 1], [5, -5]);
  const rearY = useTransform(pointerY, [-1, 1], [4, -4]);
  const labelX = useTransform(pointerX, [-1, 1], [-4, 4]);
  const labelY = useTransform(pointerY, [-1, 1], [-4, 4]);
  const glowTargetX = useTransform(pointerX, [-1, 1], [-100, 100]);
  const glowTargetY = useTransform(pointerY, [-1, 1], [-80, 80]);
  const glowX = useSpring(glowTargetX, { stiffness: 70, damping: 24, mass: 0.8 });
  const glowY = useSpring(glowTargetY, { stiffness: 70, damping: 24, mass: 0.8 });
  const pointerDepthEnabled = pointerEligible && !reduced && heroEntered;

  useEffect(() => {
    if (!pointerEligible || reduced) return;
    const fallback = window.setTimeout(() => setHeroEntered(true), 1000);
    return () => window.clearTimeout(fallback);
  }, [pointerEligible, reduced]);

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    if (!pointerDepthEnabled) return;
    const bounds = heroSectionRef.current?.getBoundingClientRect();
    if (!bounds?.width || !bounds.height) return;
    const x = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width) * 2 - 1));
    const y = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height) * 2 - 1));
    pointerX.set(x);
    pointerY.set(y);
  };

  const handlePointerLeave = () => {
    if (!pointerDepthEnabled) return;
    pointerX.set(0);
    pointerY.set(0);
  };

  const anim = reduced
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.68, ease: [0.22, 1, 0.36, 1] as const },
      };
  const whatsappUrl = buildWhatsAppUrl(siteConfig.defaultWhatsAppMessage);

  return (
    <section
      className="hero"
      data-pointer-depth={pointerDepthEnabled ? 'enabled' : 'disabled'}
      ref={heroSectionRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <motion.div className="hero__glow" aria-hidden="true" style={pointerDepthEnabled ? { x: glowX, y: glowY } : undefined} />
      <div className="shell hero__grid">
        <motion.div className="hero__copy" {...anim}>
          <p className="eyebrow">Web design and development</p>
          <h1>{siteConfig.positioning}</h1>
          <p className="hero__lead">{siteConfig.heroLead}</p>
          <div className="hero__actions">
            <ButtonLink href="/contact">Get a Quote</ButtonLink>
            <ButtonLink href={whatsappUrl} variant="secondary">
              Chat on WhatsApp
            </ButtonLink>
          </div>
          <p className="hero__micro hero__trust-line">{siteConfig.trustLine}</p>
        </motion.div>
        <motion.div
          className="hero-visual"
          initial={reduced ? false : { opacity: 0, x: 34 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
          onAnimationComplete={() => setHeroEntered(true)}
          aria-label="Website concept previews"
        >
          <motion.div className="browser browser--main" style={pointerDepthEnabled ? { x: frontX, y: frontY } : undefined}>
            <div className="browser__bar">
              <i />
              <i />
              <i />
              <span>malevstudio.com/work</span>
            </div>
            <img
              src="/images/concepts/hush-house.webp"
              width="1600"
              height="900"
              alt="Hush House backyard studio website concept"
            />
          </motion.div>
          <motion.div className="browser browser--rear" style={pointerDepthEnabled ? { x: rearX, y: rearY, rotate: -3 } : undefined}>
            <img src="/images/concepts/forge-auto.webp" width="1600" height="900" alt="" />
          </motion.div>
          <motion.div className="hero-visual__label" style={pointerDepthEnabled ? { x: labelX, y: labelY } : undefined}>
            <span>Selected concepts</span>
            <strong>Built around the business</strong>
            <ArrowDownRight size={20} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
